import { useEffect } from 'react'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { Outlet, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AccountShell } from '@/components/AccountShell'
import { SurfaceCard } from '@/components/SurfaceCard'
import { FatalError } from '@/features/errors'
import { useApplyAccountAppearance } from '@/theme/useApplyAccountAppearance'

import { AppLoadingState } from './AppLoadingState'
import { AccountAppContext } from './AccountAppContext'
import { useAccountApp } from './useAccountApp'

export const AccountLayout = () => {
    const { t } = useTranslation()
    const { tenantSlug } = useParams<{ tenantSlug: string }>()

    const appState = useAccountApp(tenantSlug)
    const { bootstrap, bootstrapError, loading, signingOut, status, handleLogout } = appState

    useApplyAccountAppearance(bootstrap?.appearance)

    const tenantName = bootstrap?.tenant.name

    useEffect(() => {
        const previousTitle = document.title

        document.title = tenantName ? t('shell.pageTitle', { tenantName }) : t('common.account')

        return () => {
            document.title = previousTitle
        }
    }, [t, tenantName])

    const profileName =
        bootstrap?.authenticated && bootstrap.user
            ? bootstrap.user.name
            : bootstrap?.tenant.name || t('shell.tenantSpace')
    const profileEmail = bootstrap?.authenticated && bootstrap.user ? bootstrap.user.email : bootstrap?.tenant.shortId
    const profilePictureUrl = bootstrap?.authenticated && bootstrap.user ? bootstrap.user.pictureUrl : null
    const showAuthenticatedShell = !!bootstrap?.authenticated

    if (loading) {
        return <AppLoadingState />
    }

    if (bootstrapError) {
        return (
            <AccountAppContext.Provider value={appState}>
                <FatalError />
            </AccountAppContext.Provider>
        )
    }

    return (
        <AccountShell
            profileName={showAuthenticatedShell ? profileName : undefined}
            profileEmail={showAuthenticatedShell ? profileEmail : undefined}
            profilePictureUrl={showAuthenticatedShell ? profilePictureUrl : null}
            onLogout={showAuthenticatedShell ? handleLogout : undefined}
            signingOut={signingOut}
            title={showAuthenticatedShell ? t('security.title') : ''}
            description={showAuthenticatedShell ? t('security.description') : ''}
            status={status}
        >
            <AccountAppContext.Provider value={appState}>
                {!bootstrap?.enabled ? (
                    <SurfaceCard title={t('states.disabledTitle')} description={t('states.disabledDescription')}>
                        <div className="text-account-secondary flex items-center gap-3 text-sm">
                            <ShieldCheckIcon className="text-account-warning h-5 w-5" />
                            {bootstrap?.tenant.name}
                        </div>
                    </SurfaceCard>
                ) : !bootstrap.authenticated ? (
                    <SurfaceCard description={t('states.redirectingDescription')}>
                        <p className="text-account-secondary text-sm leading-6">{t('states.redirectingBody')}</p>
                    </SurfaceCard>
                ) : (
                    <Outlet />
                )}
            </AccountAppContext.Provider>
        </AccountShell>
    )
}
